import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { KpiComparison, DebugQuery } from '../../types/dashboard'
import { fmt, pctChange, highlightSql, buildRunnableSql } from '../../lib/dashboardHelpers'

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)
    const handleCopy = async () => {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }
    return (
        <button onClick={handleCopy} style={{
            background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 500,
            color: copied ? '#10b981' : '#94a3b8', cursor: 'pointer', transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', gap: 4
        }}>
            {copied ? '✓ Copied' : '📋 Copy'}
        </button>
    )
}

function KpiInfoModal({ label, description, queries, onClose }: { label: string; description: string; queries?: DebugQuery[]; onClose: () => void }) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onClose])

    return createPortal(
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px', width: '100%', maxWidth: 700,
                maxHeight: '85vh', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 50px rgba(0,0,0,0.5)', cursor: 'default'
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18 }}>ℹ️</span>
                        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#e2e8f0' }}>
                            {label} Calculation
                        </h2>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8, width: 32, height: 32, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        color: '#94a3b8', fontSize: 16, cursor: 'pointer', transition: 'all 0.2s'
                    }} onMouseOver={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.2)')}
                        onMouseOut={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}>✕</button>
                </div>

                <div style={{ overflowY: 'auto', padding: '24px', flex: 1 }}>
                    <div style={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.6, marginBottom: queries && queries.length > 0 ? 24 : 0, whiteSpace: 'pre-wrap' }}>
                        {description}
                    </div>

                    {queries && queries.length > 0 && (
                        <div>
                            <h3 style={{ fontSize: 13, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12, marginTop: 0 }}>Administrator SQL</h3>
                            {queries.map((q, i) => {
                                const runnableSql = buildRunnableSql(q, i)
                                return (
                                    <div key={i} style={{
                                        marginBottom: 16, background: 'rgba(255,255,255,0.03)',
                                        borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            padding: '8px 14px', background: 'rgba(167,139,250,0.08)',
                                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                                            fontSize: 12, fontWeight: 600, color: '#a78bfa',
                                            display: 'flex', alignItems: 'center', gap: 8
                                        }}>
                                            <span style={{ flex: 1 }}>{q.label}</span>
                                            <CopyButton text={runnableSql} />
                                        </div>
                                        <pre style={{
                                            margin: 0, padding: '14px 14px', fontSize: 12.5, lineHeight: 1.6,
                                            fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                                            color: '#cbd5e1', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                                        }} dangerouslySetInnerHTML={{ __html: highlightSql(runnableSql) }} />
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    )
}

export default function KpiCard({ label, value, kpi, format, accent, lowerIsBetter, hideComparisons, showLastWeek, showYesterday, showLastYear, showLastMonth, showPctChange, showNetChange, primaryComparison, comparisonLabel, description, adminQueries }: {
    label: string; value: string; kpi: KpiComparison; format?: string; accent: string
    lowerIsBetter?: boolean; hideComparisons?: boolean; showLastWeek?: boolean; showYesterday?: boolean; showLastYear?: boolean; showLastMonth?: boolean
    showPctChange?: boolean; showNetChange?: boolean
    primaryComparison?: 'none' | 'yesterday' | 'lastWeek' | 'lastYear' | 'lastMonth'
    comparisonLabel?: string
    description?: string; adminQueries?: DebugQuery[]
}) {
    const [showInfo, setShowInfo] = useState(false)

    const formatVal = (v: number) => {
        if (format === 'percent') return fmt(v, 'percent')
        if (format === 'decimal') return fmt(v, 'decimal')
        return format === 'currency' ? fmt(v, 'currency') : fmt(v, 'int')
    }

    const renderComp = (compLabel: string, prevValue: number) => {
        const pct = pctChange(kpi.today, prevValue)
        const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat'

        let colorClass = dir
        if (lowerIsBetter && dir !== 'flat') {
            colorClass = dir === 'up' ? 'down' : 'up'
        }

        const netDelta = kpi.today - prevValue

        return (
            <div className="kpi-comparison" key={compLabel}>
                <span className={`kpi-arrow ${colorClass}`} style={{ flexShrink: 0 }}>{dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–'}</span>
                {showPctChange !== false && (
                    <span className={`kpi-pct ${colorClass}`} style={{ flexShrink: 0, marginRight: showNetChange ? 6 : undefined }}>
                        {Math.abs(pct).toFixed(1)}%
                    </span>
                )}
                {showNetChange && (
                    <span className={`kpi-pct ${colorClass}`} style={{ flexShrink: 0 }}>
                        {netDelta > 0 ? '+' : ''}{formatVal(netDelta)}
                    </span>
                )}
                <span className="comp-label-full" style={{ flexShrink: 0 }}>{compLabel}</span>
                <span className="comp-label-short" style={{ flexShrink: 0 }}>{compLabel === 'yesterday' ? 'd' : compLabel === 'last week' ? 'w' : compLabel === 'last month' ? 'm' : compLabel === 'last year' ? 'y' : compLabel.toLowerCase() === 'prior year' ? 'y' : compLabel.split(' ').map(w => w[0]?.toLowerCase()).join('')}</span>
                <span className="comp-val" style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }}>
                    ({formatVal(prevValue)})
                </span>
            </div>
        )
    }

    let valueColor: string | undefined
    if (primaryComparison && primaryComparison !== 'none') {
        const compareVal = kpi[primaryComparison]
        if (typeof compareVal === 'number' && typeof kpi.today === 'number') {
            const isBetter = lowerIsBetter ? kpi.today <= compareVal : kpi.today >= compareVal
            const isDifferent = kpi.today !== compareVal
            if (isDifferent) {
                valueColor = isBetter ? 'var(--kpi-positive)' : 'var(--kpi-negative)'
            }
        }
    }

    return (
        <div className="kpi-card" style={{ '--kpi-accent': accent } as any}>
            <div className="kpi-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {description && (
                    <button onClick={() => setShowInfo(true)} title="KPI Details" style={{
                        background: 'none', border: 'none', padding: '0', cursor: 'pointer',
                        color: 'var(--color-text-tertiary)', opacity: 0.7, fontSize: 14,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }} onMouseOver={e => e.currentTarget.style.opacity = '1'} onMouseOut={e => e.currentTarget.style.opacity = '0.7'}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                    </button>
                )}
                <span>{label}</span>
            </div>
            <div className="kpi-value" style={{ color: valueColor, transition: 'color 0.2s' }}>{value}</div>
            {!hideComparisons && (
                <div className="kpi-comparisons">
                    {showYesterday !== false && typeof kpi.yesterday === 'number' && renderComp('yesterday', kpi.yesterday)}
                    {showLastWeek !== false && typeof kpi.lastWeek === 'number' && renderComp('last week', kpi.lastWeek)}
                    {showLastMonth !== false && typeof kpi.lastMonth === 'number' && renderComp(comparisonLabel || 'last month', kpi.lastMonth)}
                    {showLastYear !== false && typeof kpi.lastYear === 'number' && renderComp('last year', kpi.lastYear)}
                </div>
            )}
            {showInfo && description && (
                <KpiInfoModal label={label} description={description} queries={adminQueries} onClose={() => setShowInfo(false)} />
            )}
        </div>
    )
}
